/**
 * Country Phone Codes
 * 
 * This file provides a comprehensive list of countries with their international dialing codes,
 * phone number length validation, and helper functions for validation and data retrieval.
 * 
 * Each country entry contains:
 * - code: International dialing code (e.g., "+1", "+90")
 * - flag: Unicode flag emoji
 * - name: Country name in English
 * - lengths: Array of valid phone number lengths (without country code)
 */

export interface CountryPhoneCode {
  code: string;
  flag: string;
  name: string;
  lengths: number[];
}

export const countryPhoneCodes: CountryPhoneCode[] = [
  // North America (+1)
  { code: "+1", flag: "🇺🇸", name: "United States", lengths: [10] },
  { code: "+1", flag: "🇨🇦", name: "Canada", lengths: [10] },
  
  // Europe
  { code: "+7", flag: "🇷🇺", name: "Russia", lengths: [10] },
  { code: "+7", flag: "🇰🇿", name: "Kazakhstan", lengths: [10] },
  { code: "+44", flag: "🇬🇧", name: "United Kingdom", lengths: [10] },
  { code: "+49", flag: "🇩🇪", name: "Germany", lengths: [10, 11] },
  { code: "+33", flag: "🇫🇷", name: "France", lengths: [9] },
  { code: "+39", flag: "🇮🇹", name: "Italy", lengths: [9, 10] },
  { code: "+34", flag: "🇪🇸", name: "Spain", lengths: [9] },
  { code: "+31", flag: "🇳🇱", name: "Netherlands", lengths: [9] },
  { code: "+32", flag: "🇧🇪", name: "Belgium", lengths: [9] },
  { code: "+41", flag: "🇨🇭", name: "Switzerland", lengths: [9] },
  { code: "+43", flag: "🇦🇹", name: "Austria", lengths: [10, 11, 12, 13] },
  { code: "+46", flag: "🇸🇪", name: "Sweden", lengths: [9] },
  { code: "+47", flag: "🇳🇴", name: "Norway", lengths: [8] },
  { code: "+45", flag: "🇩🇰", name: "Denmark", lengths: [8] },
  { code: "+358", flag: "🇫🇮", name: "Finland", lengths: [9, 10] },
  { code: "+351", flag: "🇵🇹", name: "Portugal", lengths: [9] },
  { code: "+30", flag: "🇬🇷", name: "Greece", lengths: [10] },
  { code: "+48", flag: "🇵🇱", name: "Poland", lengths: [9] },
  { code: "+420", flag: "🇨🇿", name: "Czech Republic", lengths: [9] },
  { code: "+36", flag: "🇭🇺", name: "Hungary", lengths: [9] },
  { code: "+40", flag: "🇷🇴", name: "Romania", lengths: [9] },
  { code: "+353", flag: "🇮🇪", name: "Ireland", lengths: [9] },
  { code: "+352", flag: "🇱🇺", name: "Luxembourg", lengths: [9] },
  { code: "+370", flag: "🇱🇹", name: "Lithuania", lengths: [8] },
  { code: "+371", flag: "🇱🇻", name: "Latvia", lengths: [8] },
  { code: "+372", flag: "🇪🇪", name: "Estonia", lengths: [8] },
  { code: "+385", flag: "🇭🇷", name: "Croatia", lengths: [9] },
  { code: "+386", flag: "🇸🇮", name: "Slovenia", lengths: [8] },
  { code: "+421", flag: "🇸🇰", name: "Slovakia", lengths: [9] },
  { code: "+359", flag: "🇧🇬", name: "Bulgaria", lengths: [9] },
  { code: "+381", flag: "🇷🇸", name: "Serbia", lengths: [9] },
  { code: "+382", flag: "🇲🇪", name: "Montenegro", lengths: [8] },
  { code: "+383", flag: "🇽🇰", name: "Kosovo", lengths: [8] },
  { code: "+389", flag: "🇲🇰", name: "North Macedonia", lengths: [8] },
  { code: "+387", flag: "🇧🇦", name: "Bosnia and Herzegovina", lengths: [8] },
  { code: "+355", flag: "🇦🇱", name: "Albania", lengths: [9] },
  { code: "+356", flag: "🇲🇹", name: "Malta", lengths: [8] },
  { code: "+357", flag: "🇨🇾", name: "Cyprus", lengths: [8] },
  { code: "+354", flag: "🇮🇸", name: "Iceland", lengths: [7] },
  
  // Asia
  { code: "+86", flag: "🇨🇳", name: "China", lengths: [11] },
  { code: "+91", flag: "🇮🇳", name: "India", lengths: [10] },
  { code: "+81", flag: "🇯🇵", name: "Japan", lengths: [10] },
  { code: "+90", flag: "🇹🇷", name: "Turkey", lengths: [10] },
  { code: "+82", flag: "🇰🇷", name: "South Korea", lengths: [9, 10] },
  { code: "+65", flag: "🇸🇬", name: "Singapore", lengths: [8] },
  { code: "+60", flag: "🇲🇾", name: "Malaysia", lengths: [9, 10] },
  { code: "+66", flag: "🇹🇭", name: "Thailand", lengths: [9] },
  { code: "+62", flag: "🇮🇩", name: "Indonesia", lengths: [9, 10, 11] },
  { code: "+63", flag: "🇵🇭", name: "Philippines", lengths: [10] },
  { code: "+84", flag: "🇻🇳", name: "Vietnam", lengths: [9, 10] },
  { code: "+852", flag: "🇭🇰", name: "Hong Kong", lengths: [8] },
  { code: "+853", flag: "🇲🇴", name: "Macau", lengths: [8] },
  { code: "+886", flag: "🇹🇼", name: "Taiwan", lengths: [9] },
  { code: "+92", flag: "🇵🇰", name: "Pakistan", lengths: [10] },
  { code: "+880", flag: "🇧🇩", name: "Bangladesh", lengths: [10] },
  { code: "+94", flag: "🇱🇰", name: "Sri Lanka", lengths: [9] },
  { code: "+95", flag: "🇲🇲", name: "Myanmar", lengths: [9, 10] },
  { code: "+855", flag: "🇰🇭", name: "Cambodia", lengths: [9] },
  { code: "+856", flag: "🇱🇦", name: "Laos", lengths: [10] },
  { code: "+673", flag: "🇧🇳", name: "Brunei", lengths: [7] },
  { code: "+961", flag: "🇱🇧", name: "Lebanon", lengths: [8] },
  { code: "+962", flag: "🇯🇴", name: "Jordan", lengths: [9] },
  { code: "+963", flag: "🇸🇾", name: "Syria", lengths: [9] },
  { code: "+964", flag: "🇮🇶", name: "Iraq", lengths: [10] },
  { code: "+965", flag: "🇰🇼", name: "Kuwait", lengths: [8] },
  { code: "+966", flag: "🇸🇦", name: "Saudi Arabia", lengths: [9] },
  { code: "+967", flag: "🇾🇪", name: "Yemen", lengths: [9] },
  { code: "+968", flag: "🇴🇲", name: "Oman", lengths: [8] },
  { code: "+971", flag: "🇦🇪", name: "United Arab Emirates", lengths: [9] },
  { code: "+972", flag: "🇮🇱", name: "Israel", lengths: [9] },
  { code: "+973", flag: "🇧🇭", name: "Bahrain", lengths: [8] },
  { code: "+974", flag: "🇶🇦", name: "Qatar", lengths: [8] },
  { code: "+975", flag: "🇧🇹", name: "Bhutan", lengths: [8] },
  { code: "+976", flag: "🇲🇳", name: "Mongolia", lengths: [8] },
  { code: "+977", flag: "🇳🇵", name: "Nepal", lengths: [10] },
  { code: "+992", flag: "🇹🇯", name: "Tajikistan", lengths: [9] },
  { code: "+993", flag: "🇹🇲", name: "Turkmenistan", lengths: [8] },
  { code: "+994", flag: "🇦🇿", name: "Azerbaijan", lengths: [9] },
  { code: "+995", flag: "🇬🇪", name: "Georgia", lengths: [9] },
  { code: "+996", flag: "🇰🇬", name: "Kyrgyzstan", lengths: [9] },
  { code: "+998", flag: "🇺🇿", name: "Uzbekistan", lengths: [9] },
  { code: "+93", flag: "🇦🇫", name: "Afghanistan", lengths: [9] },
  { code: "+98", flag: "🇮🇷", name: "Iran", lengths: [10] },
  
  // Middle East
  { code: "+20", flag: "🇪🇬", name: "Egypt", lengths: [10] },
  { code: "+212", flag: "🇲🇦", name: "Morocco", lengths: [9] },
  { code: "+213", flag: "🇩🇿", name: "Algeria", lengths: [9] },
  { code: "+216", flag: "🇹🇳", name: "Tunisia", lengths: [8] },
  { code: "+218", flag: "🇱🇾", name: "Libya", lengths: [9] },
  { code: "+249", flag: "🇸🇩", name: "Sudan", lengths: [9] },
  { code: "+251", flag: "🇪🇹", name: "Ethiopia", lengths: [9] },
  { code: "+254", flag: "🇰🇪", name: "Kenya", lengths: [9] },
  { code: "+255", flag: "🇹🇿", name: "Tanzania", lengths: [9] },
  { code: "+256", flag: "🇺🇬", name: "Uganda", lengths: [9] },
  { code: "+233", flag: "🇬🇭", name: "Ghana", lengths: [9] },
  { code: "+234", flag: "🇳🇬", name: "Nigeria", lengths: [10] },
  { code: "+27", flag: "🇿🇦", name: "South Africa", lengths: [9] },
  
  // Oceania
  { code: "+61", flag: "🇦🇺", name: "Australia", lengths: [9] },
  { code: "+64", flag: "🇳🇿", name: "New Zealand", lengths: [9, 10] },
  { code: "+679", flag: "🇫🇯", name: "Fiji", lengths: [7] },
  { code: "+676", flag: "🇹🇴", name: "Tonga", lengths: [7] },
  { code: "+685", flag: "🇼🇸", name: "Samoa", lengths: [7] },
  
  // Americas (excluding North America)
  { code: "+52", flag: "🇲🇽", name: "Mexico", lengths: [10] },
  { code: "+55", flag: "🇧🇷", name: "Brazil", lengths: [10, 11] },
  { code: "+54", flag: "🇦🇷", name: "Argentina", lengths: [10] },
  { code: "+56", flag: "🇨🇱", name: "Chile", lengths: [9] },
  { code: "+57", flag: "🇨🇴", name: "Colombia", lengths: [10] },
  { code: "+51", flag: "🇵🇪", name: "Peru", lengths: [9] },
  { code: "+58", flag: "🇻🇪", name: "Venezuela", lengths: [10] },
  { code: "+593", flag: "🇪🇨", name: "Ecuador", lengths: [9] },
  { code: "+595", flag: "🇵🇾", name: "Paraguay", lengths: [9] },
  { code: "+598", flag: "🇺🇾", name: "Uruguay", lengths: [8] },
  { code: "+591", flag: "🇧🇴", name: "Bolivia", lengths: [8] },
  { code: "+592", flag: "🇬🇾", name: "Guyana", lengths: [7] },
  { code: "+597", flag: "🇸🇷", name: "Suriname", lengths: [7] },
  { code: "+501", flag: "🇧🇿", name: "Belize", lengths: [7] },
  { code: "+502", flag: "🇬🇹", name: "Guatemala", lengths: [8] },
  { code: "+503", flag: "🇸🇻", name: "El Salvador", lengths: [8] },
  { code: "+504", flag: "🇭🇳", name: "Honduras", lengths: [8] },
  { code: "+505", flag: "🇳🇮", name: "Nicaragua", lengths: [8] },
  { code: "+506", flag: "🇨🇷", name: "Costa Rica", lengths: [8] },
  { code: "+507", flag: "🇵🇦", name: "Panama", lengths: [8] },
  { code: "+509", flag: "🇭🇹", name: "Haiti", lengths: [8] },
  { code: "+1-242", flag: "🇧🇸", name: "Bahamas", lengths: [7] },
  { code: "+1-246", flag: "🇧🇧", name: "Barbados", lengths: [7] },
  { code: "+1-264", flag: "🇦🇮", name: "Anguilla", lengths: [7] },
  { code: "+1-268", flag: "🇦🇬", name: "Antigua and Barbuda", lengths: [7] },
  { code: "+1-284", flag: "🇻🇬", name: "British Virgin Islands", lengths: [7] },
  { code: "+1-340", flag: "🇻🇮", name: "U.S. Virgin Islands", lengths: [7] },
  { code: "+1-345", flag: "🇰🇾", name: "Cayman Islands", lengths: [7] },
  { code: "+1-441", flag: "🇧🇲", name: "Bermuda", lengths: [7] },
  { code: "+1-649", flag: "🇹🇨", name: "Turks and Caicos", lengths: [7] },
  { code: "+1-670", flag: "🇲🇵", name: "Northern Mariana Islands", lengths: [7] },
  { code: "+1-671", flag: "🇬🇺", name: "Guam", lengths: [7] },
  { code: "+1-758", flag: "🇱🇨", name: "Saint Lucia", lengths: [7] },
  { code: "+1-767", flag: "🇩🇲", name: "Dominica", lengths: [7] },
  { code: "+1-784", flag: "🇻🇨", name: "Saint Vincent and the Grenadines", lengths: [7] },
  { code: "+1-849", flag: "🇩🇴", name: "Dominican Republic", lengths: [10] },
  { code: "+1-868", flag: "🇹🇹", name: "Trinidad and Tobago", lengths: [7] },
  { code: "+1-869", flag: "🇰🇳", name: "Saint Kitts and Nevis", lengths: [7] },
  { code: "+1-876", flag: "🇯🇲", name: "Jamaica", lengths: [7] },
  
  // Additional countries
  { code: "+353", flag: "🇮🇪", name: "Ireland", lengths: [9] },
  { code: "+44", flag: "🇯🇪", name: "Jersey", lengths: [10] },
  { code: "+44", flag: "🇬🇬", name: "Guernsey", lengths: [10] },
  { code: "+44", flag: "🇮🇲", name: "Isle of Man", lengths: [10] },
  { code: "+47", flag: "🇸🇯", name: "Svalbard and Jan Mayen", lengths: [8] },
  { code: "+298", flag: "🇫🇴", name: "Faroe Islands", lengths: [6] },
  { code: "+299", flag: "🇬🇱", name: "Greenland", lengths: [6] },
];

/**
 * Get valid phone number lengths for a given country code
 * 
 * @param countryCode - The international dialing code (e.g., "+1", "+90")
 * @returns Array of valid lengths, or [10] as default if country code not found
 * 
 * Note: This function returns the FIRST match for a country code.
 * For shared codes like "+1" (USA/Canada), it will return the lengths
 * for the first country in the array. For accurate validation when
 * countries share codes, use isValidPhoneLengthForCountry() instead.
 */
export const getValidLengths = (countryCode: string): number[] => {
  const country = countryPhoneCodes.find(c => c.code === countryCode);
  return country ? country.lengths : [10];
};

/**
 * Validate if a phone number has a valid length for a given country code
 * 
 * @param phoneNumber - The phone number to validate (without country code)
 * @param countryCode - The international dialing code (e.g., "+1", "+90")
 * @returns true if the phone number length is valid, false otherwise
 * 
 * Note: This function uses getValidLengths(), which only returns the first
 * match for shared country codes. For more accurate validation, use
 * isValidPhoneLengthForCountry() with the full country object.
 */
export const isValidPhoneLength = (phoneNumber: string, countryCode: string): boolean => {
  const validLengths = getValidLengths(countryCode);
  return validLengths.includes(phoneNumber.length);
};

/**
 * Validate if a phone number has a valid length for a specific country object
 * 
 * This is more accurate than isValidPhoneLength() when countries share
 * the same dialing code.
 * 
 * @param phoneNumber - The phone number to validate (without country code)
 * @param country - The full country object from countryPhoneCodes array
 * @returns true if the phone number length is valid, false otherwise
 */
export const isValidPhoneLengthForCountry = (phoneNumber: string, country: CountryPhoneCode | null | undefined): boolean => {
  const validLengths = country?.lengths || [10];
  return validLengths.includes(phoneNumber.length);
};

/**
 * Get the maximum valid phone number length for a given country code
 * 
 * @param countryCode - The international dialing code (e.g., "+1", "+90")
 * @returns The maximum valid length, or 10 as default if country code not found
 * 
 * Use Case: Useful for setting maximum input length in phone number input fields.
 */
export const getMaxLength = (countryCode: string): number => {
  const validLengths = getValidLengths(countryCode);
  return Math.max(...validLengths);
};

/**
 * Get the maximum valid phone number length for a specific country object
 * 
 * Similar to isValidPhoneLengthForCountry(), this function provides accurate
 * maximum length for specific countries when multiple countries share the same dialing code.
 * 
 * @param country - The full country object from countryPhoneCodes array
 * @returns The maximum valid length, or 10 as default if country is null/undefined
 */
export const getMaxLengthForCountry = (country: CountryPhoneCode | null | undefined): number => {
  const validLengths = country?.lengths || [10];
  return Math.max(...validLengths);
};

/**
 * Find a country by its name (case-insensitive)
 * 
 * @param countryName - The country name to search for
 * @returns The country object if found, undefined otherwise
 */
export const findCountryByName = (countryName: string): CountryPhoneCode | undefined => {
  return countryPhoneCodes.find(c => 
    c.name.toLowerCase() === countryName.toLowerCase()
  );
};

/**
 * Find all countries with a given country code
 * 
 * @param countryCode - The international dialing code (e.g., "+1")
 * @returns Array of all countries with the given code
 */
export const findCountriesByCode = (countryCode: string): CountryPhoneCode[] => {
  return countryPhoneCodes.filter(c => c.code === countryCode);
};

/**
 * Find a country by code and name (for shared codes)
 * 
 * @param countryCode - The international dialing code
 * @param countryName - The country name
 * @returns The country object if found, undefined otherwise
 */
export const findCountryByCodeAndName = (countryCode: string, countryName: string): CountryPhoneCode | undefined => {
  return countryPhoneCodes.find(c => 
    c.code === countryCode && c.name.toLowerCase() === countryName.toLowerCase()
  );
};

